import { lazy, Suspense, useEffect } from "react";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import { Switch, Route, Router as WouterRouter, useLocation, Redirect, useSearch } from "wouter";
import { useCapitalAwareLocation } from "@/lib/fundLinks";
import { installAnalyticsConsentSync, synchronizeAnalyticsConsent } from "./lib/analytics";
import { useAnalytics } from "./hooks/use-analytics";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { useAuth } from "@/hooks/useAuth";
import { PageLoader, AdminPageLoader } from "@/components/PageLoader";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";
import { MobileBottomNav } from "@/components/navigation/MobileBottomNav";
import { AdminRoleProvider } from "@/contexts/AdminRoleContext";
import { DomainProvider, useDomain } from "@/contexts/DomainContext";
import { PersonalizationTrackerProvider } from "@/hooks/usePersonalizationTracker";
import { AnalyticsConsentBanner } from "@/components/AnalyticsConsentBanner";

import NotFound from "@/pages/not-found";
import Landing from "@/pages/Landing";
import Home from "@/pages/Home";
import { isChiefInAIDomain } from "@/lib/domainUtils";

const ChiefInAI = lazy(() => import("@/pages/ChiefInAI"));

// Domain route modules — each owns its lazy imports and <Route>s
import { AdminRoutes } from "@/routes/adminRoutes";
import { CohortRoutes } from "@/routes/cohortRoutes";
import { PublicRoutes } from "@/routes/publicRoutes";
import { MemberRoutes } from "@/routes/memberRoutes";
import { MediaRoutes } from "@/routes/mediaRoutes";
import { NetworkRoutes } from "@/routes/networkRoutes";
import { CompanionRoutes } from "@/routes/companionRoutes";

const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Admin = lazy(() => import("@/pages/Admin"));

// Component to handle ?front=true for admins to preview the anonymous landing page
function HomeOrLanding() {
  const { user } = useAuth();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const showFrontPage = searchParams.get("front") === "true";

  if (user?.isAdmin && showFrontPage) {
    return <Landing />;
  }

  return <Home />;
}

function LazyRoute({ component: Component, admin = false, ...props }: { component: React.LazyExoticComponent<React.ComponentType<any>>; admin?: boolean;[key: string]: any }) {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={admin ? <AdminPageLoader /> : <PageLoader />}>
        <Component {...props} />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

function AdminRoute({ component: Component }: { component: React.LazyExoticComponent<React.ComponentType<any>> }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <AdminPageLoader />;
  }

  if (!user?.isAdmin) {
    return <Redirect to="/" />;
  }

  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<AdminPageLoader />}>
        <Component />
      </Suspense>
    </ChunkErrorBoundary>
  );
}

function OnboardingRedirect({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const [location] = useLocation();

  if (isLoading) {
    return <>{children}</>;
  }

  // The Event Companion is a standalone app (its own auth/profile surface). New
  // users who sign up inline there must stay in-app, so never bounce companion
  // routes to the main onboarding flow.
  if (user && !user.onboardingCompleted && location !== "/onboarding" && !location.startsWith("/companion/")) {
    // Persist skip state in localStorage so it survives tab close
    const SKIP_COUNT_KEY = "cit_onboarding_skip_count";
    const SKIP_TS_KEY = "cit_onboarding_skip_ts";
    const MAX_SKIPS = 2;

    const skipCount = parseInt(localStorage.getItem(SKIP_COUNT_KEY) || "0", 10);
    const skipTs = parseInt(localStorage.getItem(SKIP_TS_KEY) || "0", 10);
    const hoursSinceSkip = (Date.now() - skipTs) / (1000 * 60 * 60);

    // After 2 skips, never redirect to onboarding again
    if (skipCount >= MAX_SKIPS) {
      return <>{children}</>;
    }
    // If skipped recently (within 48h), don't re-prompt
    if (skipCount > 0 && hoursSinceSkip < 48) {
      return <>{children}</>;
    }

    const innerRedirect = new URLSearchParams(window.location.search).get("redirect");
    const finalDest = innerRedirect || (location + window.location.search);
    const redirectParam = finalDest && finalDest !== "/" ? `?redirect=${encodeURIComponent(finalDest)}` : "";
    return <Redirect to={`/onboarding${redirectParam}`} />;
  }

  return <>{children}</>;
}

function HashRedirect({ to }: { to: string }) {
  const [, setLocation] = useLocation();
  useEffect(() => {
    // SPA navigation preserves React Query cache, auth state, and avoids a full page reload.
    // wouter's setLocation accepts paths with hash fragments; the existing useScrollToHash
    // hook in <Router/> picks up the hash and scrolls to the right anchor.
    setLocation(to);
  }, [to, setLocation]);
  return null;
}

function DomainRouteGuard({ children }: { children: React.ReactNode }) {
  const { domain, isLoading, isRouteAllowed } = useDomain();
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (isLoading) return;
    if (!isRouteAllowed(location) && !location.startsWith("/admin")) {
      setLocation(domain.defaultRoute || "/");
    }
  }, [location, isLoading, isRouteAllowed, domain.defaultRoute, setLocation]);

  return <>{children}</>;
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

const MEDIA_SUBROUTES = new Set(["articles", "news", "daily-brief", "topics", "trending", "funding", "executive-moves", "advertise", "podcasts"]);

const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
function MediaArticleGuard() {
  const [location] = useLocation();
  const segment = location.split("/")[2]?.split("?")[0];
  if (segment && MEDIA_SUBROUTES.has(segment)) return null;
  return <LazyRoute component={ArticlePage} />;
}

// Shared props passed to every route module
const routeHelpers = { LazyRoute, AdminRoute, HashRedirect, MediaArticleGuard } as const;

function Router() {
  const { user, isAuthenticated, isLoading } = useAuth();

  useAnalytics();
  useScrollToHash({ offset: 80 });

  const helpers = { ...routeHelpers, user };

  return (
    <WouterRouter hook={useCapitalAwareLocation}>
    <DomainRouteGuard>
      <ScrollToTop />
      {isLoading || !isAuthenticated ? (
        <Switch>
          <Route path="/">{() => isChiefInAIDomain() ? <LazyRoute component={ChiefInAI} /> : <Landing />}</Route>
          {PublicRoutes(helpers)}
          {MediaRoutes(helpers)}
          {NetworkRoutes(helpers)}
          {MemberRoutes(helpers)}
          {CohortRoutes(helpers)}
          {CompanionRoutes(helpers)}
          <Route component={NotFound} />
        </Switch>
      ) : (
        <OnboardingRedirect>
          <Switch>
            <Route path="/onboarding">{() => <LazyRoute component={Onboarding} />}</Route>
            <Route path="/">{() => isChiefInAIDomain() ? <LazyRoute component={ChiefInAI} /> : <HomeOrLanding />}</Route>
            <Route path="/home" component={Home} />
            {/* Admin shell — /admin/* sub-paths are owned by AdminRoutes */}
            <Route path="/admin">{() => <AdminRoute component={Admin} />}</Route>
            {AdminRoutes(helpers)}
            {PublicRoutes(helpers)}
            {MediaRoutes(helpers)}
            {NetworkRoutes(helpers)}
            {MemberRoutes(helpers)}
            {CohortRoutes(helpers)}
            {CompanionRoutes(helpers)}
            <Route component={NotFound} />
          </Switch>
        </OnboardingRedirect>
      )}
    </DomainRouteGuard>
    </WouterRouter>
  );
}

function App() {
  useEffect(() => {
    const removeConsentSync = installAnalyticsConsentSync();
    synchronizeAnalyticsConsent();
    return removeConsentSync;
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="chief-in-tech-theme">
        <DomainProvider>
          <PersonalizationTrackerProvider>
            <TooltipProvider>
              <Toaster />
              <Router />
              <MobileBottomNav />
              <AnalyticsConsentBanner />
            </TooltipProvider>
          </PersonalizationTrackerProvider>
        </DomainProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
